import { BeneficiaryType } from "../types/BeneficiaryType";
import { MezBeneficiary } from "./estateProfile.model";
import { Person, getPersonDisplayName } from "@services/zok";
export { US_STATES_AND_TERRITORIES } from "@weps/shared-constants";

export interface DropdownStateChangeEvent {
  activeItems?: string[];
}

export type BeneficiarySectionType = "PRIMARY" | "CONTINGENT";

export interface PersonUpdateData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  stateOfResidence: string;
}

export interface ExistingBeneficiary {
  kind: "existing";
  householdMemberId: string;
  type: BeneficiarySectionType;
  distributionPercentage: number;
  beneficiaryType: BeneficiaryType;
}

export interface NewBeneficiary {
  kind: "new";
  householdMemberId: null;
  personData: PersonUpdateData;
  type: BeneficiarySectionType;
  distributionPercentage: number;
  beneficiaryType: BeneficiaryType;
}

export type Beneficiary = ExistingBeneficiary | NewBeneficiary;

export interface EditBeneficiaryPayload {
  beneficiary: ExistingBeneficiary;
  personData: PersonUpdateData;
}

export interface BeneficiaryState {
  primaryBeneficiaries: Beneficiary[];
  backupBeneficiaries: Beneficiary[];
}

export function createEmptyBeneficiaryState(): BeneficiaryState {
  return {
    primaryBeneficiaries: [],
    backupBeneficiaries: [],
  };
}

export function calculateDistributionTotal(beneficiaries: Beneficiary[]): number {
  return beneficiaries.reduce((sum, bene) => sum + (bene.distributionPercentage || 0), 0);
}

/**
 * Restores BeneficiaryState from MEZ GET response beneficiaries.
 * MEZ returns { associatedMemberId, type, distributionPercentage } — we map these
 * to Beneficiary[], splitting into primary and backup arrays.
 * Person data (name, DOB, state) is looked up from ZOK householdPeople when needed.
 */
export function restoreBeneficiaryState(mezBeneficiaries: MezBeneficiary[]): BeneficiaryState {
  const primary: Beneficiary[] = [];
  const backup: Beneficiary[] = [];

  for (const bene of mezBeneficiaries) {
    const selection: ExistingBeneficiary = {
      kind: "existing",
      householdMemberId: bene.associatedMemberId,
      type: bene.type,
      distributionPercentage: bene.distributionPercentage,
      beneficiaryType: BeneficiaryType.INDIVIDUAL_BY_NAME,
    };

    if (bene.type === "CONTINGENT") {
      backup.push(selection);
    } else {
      primary.push(selection);
    }
  }

  return { primaryBeneficiaries: primary, backupBeneficiaries: backup };
}

// sm breakpoint: ≥576px uses medium density (24px padding), <576px uses small (16px padding)
export function getCardDensity(): "small" | "medium" {
  return window.innerWidth < 576 ? "small" : "medium";
}

/**
 * Gets display name for a beneficiary.
 * For new beneficiaries, uses personData.firstName/lastName.
 * For existing beneficiaries, looks up Person from householdPeople.
 */
export function getBeneficiaryDisplayName(
  beneficiary: Beneficiary | null,
  householdPeople: Person[],
): string {
  if (!beneficiary) return "";

  if (beneficiary.kind === "new") {
    return `${beneficiary.personData.firstName} ${beneficiary.personData.lastName}`;
  }

  const person = householdPeople.find((p) => p.householdMemberId === beneficiary.householdMemberId);
  return person ? getPersonDisplayName(person) : "";
}
